/**
 * MemberEditDialog — owner-only role change and removal for a single member.
 *
 * There is no shared Dialog primitive in this repo, so the overlay/card and its
 * dismiss behaviour (backdrop click, Escape, Cancel) are built inline, matching
 * CreateOrgDialog.
 *
 * The server is the authority on both actions (Better Auth org access control,
 * pin-verified 1.6.23): only a member holding the creator role ('owner' here)
 * may grant or clear owner, and neither update nor remove may leave the
 * workspace without an owner. This dialog mirrors those rules in the UI — it
 * does not replace them, so server refusals surface as toasts.
 */
import { useMutation } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { authClient } from '@/lib/auth-client';
import type { OrgMember } from '@/lib/use-active-org';

/** The two roles this product exposes; invites are always sent as 'member'. */
const ROLE_OPTIONS = [
  { value: 'member', label: 'Member', hint: 'Can use the workspace and its sources.' },
  {
    value: 'owner',
    label: 'Owner',
    hint: 'Can also invite and remove people, change roles, and manage the workspace.',
  },
] as const;

/**
 * Mount this keyed by member id so each opening starts from that member's
 * current role — there is no reset effect, the fresh mount is the reset.
 */
export function MemberEditDialog({
  member,
  onClose,
  onChanged,
}: {
  member: OrgMember;
  onClose: () => void;
  onChanged: () => void;
}) {
  const [role, setRole] = useState(member.role);
  const [confirmingRemove, setConfirmingRemove] = useState(false);

  const updateRole = useMutation({
    mutationFn: async (nextRole: string) => {
      const { error } = await authClient.organization.updateMemberRole({
        memberId: member.id,
        role: nextRole,
      });
      if (error) throw new Error(error.message ?? 'Could not update role');
    },
    onSuccess: () => {
      toast.success(`${member.user.name} is now ${role === 'owner' ? 'an owner' : 'a member'}`);
      onChanged();
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const remove = useMutation({
    mutationFn: async () => {
      const { error } = await authClient.organization.removeMember({ memberIdOrEmail: member.id });
      if (error) throw new Error(error.message ?? 'Could not remove member');
    },
    onSuccess: () => {
      toast.success(`${member.user.name} removed from the workspace`);
      onChanged();
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const busy = updateRole.isPending || remove.isPending;

  // Escape dismisses — but never mid-request.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busy) onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [busy, onClose]);

  // A role outside the pair above (nothing in this product creates one) is kept
  // as an option so saving can't silently rewrite it.
  const options: { value: string; label: string; hint?: string }[] = ROLE_OPTIONS.some(
    (o) => o.value === member.role,
  )
    ? [...ROLE_OPTIONS]
    : [...ROLE_OPTIONS, { value: member.role, label: member.role }];

  return createPortal(
    // biome-ignore lint/a11y/useKeyWithClickEvents: backdrop click-to-dismiss; Escape and the Cancel button are the keyboard paths
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="member-edit-title"
      onClick={(e) => {
        if (e.target === e.currentTarget && !busy) onClose();
      }}
    >
      <div className="w-full max-w-md rounded-xl border border-border bg-background p-6 shadow-lg">
        <h2 id="member-edit-title" className="text-base font-semibold">
          Manage member
        </h2>
        <div className="mt-4 flex items-center gap-3">
          <div className="flex h-[34px] w-[34px] shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-primary-foreground">
            {member.user.name.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{member.user.name}</p>
            <p className="truncate text-xs text-muted-foreground">{member.user.email}</p>
          </div>
        </div>

        <div className="mt-5 space-y-1.5">
          <Label htmlFor="member-role">Role</Label>
          <Select
            id="member-role"
            value={role}
            disabled={busy}
            onChange={(e) => setRole(e.target.value)}
          >
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </Select>
          <p className="text-xs text-muted-foreground">
            {options.find((o) => o.value === role)?.hint ?? ''}
          </p>
        </div>

        <div className="mt-6 flex items-center justify-between gap-2">
          {confirmingRemove ? (
            <Button
              type="button"
              variant="destructive"
              size="sm"
              disabled={busy}
              onClick={() => remove.mutate()}
            >
              {remove.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm remove'}
            </Button>
          ) : (
            <Button
              type="button"
              variant="destructiveOutline"
              size="sm"
              disabled={busy}
              onClick={() => setConfirmingRemove(true)}
            >
              Remove
            </Button>
          )}
          <div className="flex gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={busy || role === member.role}
              onClick={() => updateRole.mutate(role)}
            >
              {updateRole.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
            </Button>
          </div>
        </div>
        {confirmingRemove ? (
          <p className="mt-3 text-xs text-muted-foreground">
            {member.user.name} loses access to this workspace immediately. They can only return via
            a new invitation.
          </p>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
